
import { defineComponent, computed } from '@vue/composition-api';
import packagesData from '~/data/packages';
import fightcampBagRing from '~/data/includes/fightcamp-bag-ring.js';
import Section from '~/components/v5/Section.vue';
import PackageCard from '~/components/v5/PackageCard.vue';
import FCSwiper from '~/components/FCSwiper.vue';
import { isMobileEffect } from '~/effects/matchMedia';
import { isProdEnvironment } from '~/utils/helpers';

export default defineComponent({
  name: 'StartTrainingSection',
  components: {
    Section,
    PackageCard,
    FCSwiper,
  },
  setup() {
    const isMobile = isMobileEffect();
    const nameColorMap: Record<string, string> = {
      Connect: 'package-card__title--gray',
      Core: 'package-card__title--navy',
      Elite: 'package-card__title--red',
    };

    const getNameColor = (name: string): string => nameColorMap[name] || '';

    const getPackageIncludes = (packageTitle: string, packageIncludes: any[]) => {
      if (packageTitle === 'Connect') {
        // Connect shows all items with check-circle icon
        return packageIncludes.map((item) => ({
          text: item.productName,
          isInheritedText: false,
          icon: 'check-circle',
        }));
      }

      if (packageTitle === 'Core') {
        // Core shows "Everything from Connect" (check) + additional items (plus)
        const connectItems = packagesData[0].includes.length;
        const additionalItems = packageIncludes.slice(connectItems);

        return [
          { text: 'Everything from FightCamp Connect', isInheritedText: true, icon: 'check-circle' },
          ...additionalItems.map((item) => ({
            text: item.productName,
            isInheritedText: false,
            icon: 'plus-circle',
          })),
        ];
      }

      if (packageTitle === 'Elite') {
        // Elite shows "Everything from Connect & Core" (check) + additional items (plus)
        const coreItems = packagesData[1].includes.length;
        const additionalItems = packageIncludes.slice(coreItems);

        return [
          { text: 'Everything from FightCamp Connect & Core', isInheritedText: true, icon: 'check-circle' },
          ...additionalItems.map((item) => ({
            text: item.productName,
            isInheritedText: false,
            icon: 'plus-circle',
          })),
        ];
      }

      return packageIncludes.map((item) => ({
        text: item.productName,
        isInheritedText: false,
        icon: 'check-circle',
      }));
    };

    const { $store } = useNuxtApp();

    // Modify packages for Canadian customers
    const packages = computed(() => {
      const isCanada = $store.getters.isGeoLocatedCanada;

      return packagesData.map((pkg, index) => {
        // Add bag ring to Core package for Canadian customers
        if (isCanada && index === 1 && pkg.title === 'Core') {
          return {
            ...pkg,
            includes: [
              ...pkg.includes,
              fightcampBagRing,
            ],
            assets: [
              {
                src: 'https://a.storyblok.com/f/152150/1200x1120/9e899fdccd/core_package_ring.jpg',
                alt: 'FightCamp Core Package with Ring',
              },
            ],
          };
        }

        return pkg;
      });
    });

    const connectGID = isProdEnvironment() ? packagesData[0].prodShopifyId : packagesData[0].testShopifyId;
    const connectPackage = computed(() => $store.getters.getProductByGID(connectGID));

    const prices = computed(() => {
      const product = connectPackage.value;
      const asLowAsPrice = product?.variants?.[0]?.price || null;
      const comparedAtPrice = product?.variants?.[0]?.compareAtPrice || null;
      return { asLowAsPrice, comparedAtPrice };
    });

    return {
      isMobile,
      packages,
      getNameColor,
      getPackageIncludes,
      prices,
    };
  },
});
